import { useRef, useEffect } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useSidebar } from "@/hooks/use-sidebar";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { toast } from "sonner";
import gsap from "gsap";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MONITORING_CONFIGS,
  MONITORING_TYPES,
  createMonitoringIcon,
} from "@/constants/monitoring";
import { useQueryClient } from "@tanstack/react-query";

interface NavigationItem {
  icon?: React.ReactNode;
  path?: string;
  label?: string;
  onClick?: () => void;
  isGap?: boolean;
  isSeparator?: boolean;
}

export default function Sidebar({ isDarkMode }: { isDarkMode?: boolean }) {
  const navigate = useNavigate();
  const router = useRouter();
  const currentPath = router.state.location.pathname;
  const { isExpanded, setIsExpanded } = useSidebar();
  const { user, isManager, isHashKeyUser, isAdmin } = useAuthStore();
  const queryClient = useQueryClient();
  const asideRef = useRef<HTMLElement>(null);

  // Stagger-animate all nav items on first mount
  useEffect(() => {
    if (!asideRef.current) return;
    const ctx = gsap.context(() => {
      const items = asideRef.current!.querySelectorAll(".nav-stagger-item");
      if (!items.length) return;
      gsap.fromTo(
        items,
        { opacity: 0, x: -18, y: 4 },
        {
          opacity: 1,
          x: 0,
          y: 0,
          duration: 0.42,
          stagger: 0.07,
          delay: 0.18,
          ease: "power3.out",
          clearProps: "transform,opacity",
        },
      );
    }, asideRef);
    return () => ctx.revert();
  }, []);

  const handleNavigate = (path: string) => () => navigate({ to: path });

  const handleLogout = async () => {
    try {
      queryClient.clear();
      localStorage.removeItem("auth-storage");
      toast.success("Logout successful");
    } catch {
      toast.error("Logout failed");
    } finally {
      useAuthStore.setState({
        user: null,
        token: null,
        refresh_token: null,
        expiresAt: null,
      });
      localStorage.removeItem("auth-storage");
      navigate({ to: "/login" });
    }
  };

  // --- Colors based on theme ---
  const baseText = isDarkMode ? "text-neutral-200" : "text-neutral-950";
  const hoverBg = isDarkMode ? "hover:bg-neutral-950" : "hover:bg-neutral-200";
  const activeBg = isDarkMode ? "bg-[#3A322C]" : "bg-neutral-100";
  const asideBg = isDarkMode ? "bg-admin-bg" : "bg-brand-50";

  // --- Nav Configs ---
  const DARK_MAIN_NAV: NavigationItem[] = [
    { isGap: true },
    {
      icon: <i className={`ri-dashboard-line ${baseText} text-[20px]`} />,
      path: "/dashboard",
      label: "Dashboard",
    },
    {
      icon: <i className={`ri-group-line ${baseText} text-[20px]`} />,
      path: "/groups",
      label: "Groups",
    },
    {
      icon: <i className={`ri-lock-2-line ${baseText} text-[20px]`} />,
      path: "/lock",
      label: "Lock",
    },
    {
      icon: <i className={`ri-shield-check-line ${baseText} text-[20px]`} />,
      path: "/security",
      label: "Security",
    },
    // Only show management features for non-hash-key managers
    ...(isManager && !isHashKeyUser()
      ? [
          {
            icon: <i className={`ri-team-line ${baseText} text-[20px]`} />,
            path: "/manager/subscription-management",
            label: "Team Management",
          },
          { isSeparator: true },
        ]
      : []),

    {
      icon: <i className={`ri-sound-module-line ${baseText} text-[20px]`} />,
      path: "/modules",
      label: "Modules",
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.KEYWORD,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.KEYWORD].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.KEYWORD].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.INFLUENCER,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.INFLUENCER].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.INFLUENCER].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.SOURCE,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.SOURCE].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.SOURCE].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.PULSE,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.PULSE].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.PULSE].label,
    },
    { isGap: true },
  ];

  const LIGHT_MAIN_NAV: NavigationItem[] = [
    ...(isAdmin
      ? [
          {
            icon: <i className={`ri-dashboard-line ${baseText} text-[20px]`} />,
            path: "/admin/dashboard",
            label: "Admin Dashboard",
          },
          {
            icon: <i className={`ri-group-line ${baseText} text-[20px]`} />,
            path: "/admin/client",
            label: "Clients",
          },
          { isSeparator: true },
        ]
      : []),

    // Only show management features for non-hash-key managers
    ...(isManager && !isHashKeyUser()
      ? [
          {
            icon: <i className={`ri-group-line ${baseText} text-[20px]`} />,
            path: "/manager/team-management",
            label: "User Management",
          },
          {
            icon: (
              <i className={`ri-flashlight-line ${baseText} text-[20px]`} />
            ),
            path: "/manager/subscription-management",
            label: "Subscription",
          },
          {
            icon: (
              <i className={`ri-calculator-line ${baseText} text-[20px]`} />
            ),
            path: "/plans",
            label: "Plans",
          },
          { isSeparator: true },
        ]
      : []),
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.KEYWORD,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.KEYWORD].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.KEYWORD].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.INFLUENCER,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.INFLUENCER].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.INFLUENCER].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.SOURCE,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.SOURCE].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.SOURCE].label,
    },
    {
      icon: createMonitoringIcon(
        MONITORING_TYPES.PULSE,
        "text-[20px]",
        isDarkMode,
      ),
      path: MONITORING_CONFIGS[MONITORING_TYPES.PULSE].path,
      label: MONITORING_CONFIGS[MONITORING_TYPES.PULSE].label,
    },
  ];

  const FOOTER_NAV: NavigationItem[] = [
    {
      icon: <i className={`ri-git-repository-line ${baseText} text-[20px]`} />,
      path: "/documentation",
      label: "Documentation",
    },
    {
      icon: <i className={`ri-git-branch-line ${baseText} text-[20px]`} />,
      path: "/changelogs",
      label: "Changelogs",
    },
    {
      icon: <i className={`ri-heart-pulse-line ${baseText} text-[20px]`} />,
      path: "/service-status",
      label: "Status",
    },
    {
      icon: <i className={`ri-settings-line ${baseText} text-[20px]`} />,
      path: "/settings",
      label: "Settings",
    },
    { isSeparator: true },
    {
      icon: <i className={`ri-logout-circle-r-line ${baseText} text-[20px]`} />,
      label: "Logout",
      onClick: handleLogout,
    },
  ];

  const MAIN_NAV = isDarkMode ? DARK_MAIN_NAV : LIGHT_MAIN_NAV;

  // --- Helpers ---
  const isActive = (path?: string) => {
    if (!path) return false;
    if (currentPath === path) return true;
    return currentPath.startsWith(path + "/");
  };

  // Both expanded and collapsed: items are always left-aligned.
  // Icons never shift horizontally — the sidebar width alone drives the animation.
  const getNavClass = (path?: string) =>
    `flex h-[36px] w-full items-center gap-3 rounded-lg transition-colors duration-150 cursor-pointer ${hoverBg} ${
      isActive(path) ? activeBg : ""
    }`;

  // Label: opacity exits fast (100ms) so text disappears before max-width clips it.
  // On expand, max-width opens first then opacity fades in with a slight delay.
  const getLabelStyle = (): React.CSSProperties =>
    isExpanded
      ? { transition: "max-width 0.28s ease-out, opacity 0.22s ease-out 0.08s" }
      : { transition: "max-width 0.28s ease-in, opacity 0.1s ease-in" };

  const renderNavItem = (item: NavigationItem, idx: number) => {
    if (item.isGap) {
      return <div key={`gap-${idx}`} className="h-6" />;
    }
    if (item.isSeparator) {
      return (
        <div
          key={`sep-${idx}`}
          className={`m-auto h-[1px] border-t border-neutral-300 transition-[width] duration-300 ease-out ${
            isExpanded ? "w-[90%]" : "w-[20px]"
          }`}
        />
      );
    }
    if (!item.icon) return null;

    const onClick =
      item.onClick || (item.path ? handleNavigate(item.path) : undefined);

    return (
      <TooltipProvider key={item.path || item.label}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`${getNavClass(item.path)} nav-stagger-item overflow-hidden pl-2`}
              onClick={onClick}
            >
              <div className="flex-shrink-0">{item.icon}</div>
              <span
                className={`text-sm whitespace-nowrap ${baseText} ${
                  isActive(item.path) ? "font-bold" : "font-medium"
                } ${isExpanded ? "max-w-[200px] opacity-100" : "max-w-0 overflow-hidden opacity-0"}`}
                style={getLabelStyle()}
              >
                {item.label}
              </span>
            </div>
          </TooltipTrigger>
          {!isExpanded && item.label && (
            <TooltipContent side="right">
              <p>{item.label}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <aside
      ref={asideRef}
      className={`flex h-screen shrink-0 flex-col justify-between overflow-hidden px-3 py-3 transition-[width] duration-300 ease-out ${
        isExpanded ? "w-[240px]" : "w-[60px]"
      } ${asideBg}`}
    >
      {/* Top Section — always w-full, no centering */}
      <div className="flex h-full w-full flex-col gap-[40px]">
        {/* Toggle button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mb-[12px] w-full pl-2 outline-none"
              >
                <div className="mt-1 flex w-full cursor-pointer items-center gap-3">
                  <i
                    className={`ri-menu-line text-[20px] ${baseText} flex-shrink-0 transition-transform duration-300 ease-out ${
                      isExpanded ? "rotate-0" : "rotate-180"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium whitespace-nowrap ${baseText} ${
                      isExpanded
                        ? "max-w-[200px] opacity-100"
                        : "max-w-0 overflow-hidden opacity-0"
                    }`}
                    style={getLabelStyle()}
                  >
                    Menu
                  </span>
                </div>
              </button>
            </TooltipTrigger>
            {!isExpanded && (
              <TooltipContent side="right">
                <p>Expand sidebar</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {/* Main nav — no centering, items fill full width */}
        <div className="flex w-full flex-col gap-[4px]">
          {MAIN_NAV.map(renderNavItem)}
        </div>
      </div>

      {/* Footer Section — no centering */}
      <div className="flex w-full flex-col gap-[12px]">
        {FOOTER_NAV.map(renderNavItem)}

        {/* User profile */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`${getNavClass("/profile")} overflow-hidden pl-2`}
              >
                <div
                  className={`flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-full border-[1px] ${
                    isDarkMode
                      ? "border-neutral-600 bg-neutral-950"
                      : "border-neutral-300 bg-neutral-200"
                  }`}
                >
                  {user?.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt="profile"
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    <i className={`ri-user-line text-[16px] ${baseText}`} />
                  )}
                </div>
                <span
                  className={`text-sm font-medium whitespace-nowrap ${baseText} ${
                    isExpanded
                      ? "max-w-[200px] opacity-100"
                      : "max-w-0 overflow-hidden opacity-0"
                  }`}
                  style={getLabelStyle()}
                >
                  {user?.first_name || "User"} {user?.last_name || ""}
                </span>
              </div>
            </TooltipTrigger>
            {!isExpanded && (
              <TooltipContent side="right">
                <p>
                  {user?.first_name
                    ? `${user.first_name} ${user?.last_name || ""}`.trim()
                    : "User Profile"}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </aside>
  );
}
